import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageBanner from "@/components/PageBanner";
import ProductCard from "@/components/ProductCard";
import { Leaf, Sparkles, GraduationCap, Gift, Shirt, Wheat } from "lucide-react";

/**
 * Products — category-first browsing.
 * - Every visitor lands on a specific category (default: Health).
 * - Only category tabs are shown; no search UI on the public storefront.
 */

const CATS = [
  { k: "health",        l: "Health & Wellness",  icon: Leaf         },
  { k: "education",     l: "Education",          icon: GraduationCap},
  { k: "agriculture",   l: "Agriculture",        icon: Wheat        },
  { k: "fashion",       l: "Fashion & Apparel",  icon: Shirt        },
  { k: "personal_care", l: "Personal Care",      icon: Sparkles     },
  { k: "combo",         l: "Combos",             icon: Gift         },
];

const DEFAULT_CAT = "health";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const cat = params.get("category") || DEFAULT_CAT;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const query = cat === "personal_care" ? "personal_care,beauty" : cat;
    api
      .get(`/products?category=${encodeURIComponent(query)}&limit=48`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [cat]);

  const active = CATS.find((c) => c.k === cat) || CATS[0];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <PageBanner
        label="Our Products"
        title={active.l}
        subtitle="Shop authentic Samara Wiz products — GST-billed, pan-India delivery, associate-supported customer service."
        crumbs={[
          { label: "Home", to: "/" },
          { label: "Products", to: "/products?category=health" },
          { label: active.l },
        ]}
      />

      {/* Category tabs */}
      <section className="bg-ivory border-b border-line sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center gap-2 overflow-x-auto">
          {CATS.map((c) => {
            const Icon = c.icon;
            const isActive = c.k === cat;
            return (
              <button
                key={c.k}
                data-testid={`cat-tab-${c.k}`}
                onClick={() => setParams({ category: c.k })}
                className={`shrink-0 inline-flex items-center gap-2 px-4 py-2.5 text-sm rounded-sm border transition-all ${
                  isActive
                    ? "bg-royal text-gold border-royal shadow-sm"
                    : "bg-white text-royal/80 border-line hover:border-gold hover:text-royal"
                }`}
              >
                <Icon size={14} /> {c.l}
              </button>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {cat === "fashion" && !loading && !items.length ? (
          <ComingSoonBanner
            title="Fashion & Apparel"
            subtitle="Saree · Salwar Suit · Suit Length"
            note="Handloom sarees, women's salwar suits, men's suit length and pant-shirt combos — releasing soon."
            img="https://images.pexels.com/photos/994523/pexels-photo-994523.jpeg"
          />
        ) : loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={`prod-skel-${i}`} className="aspect-square bg-ivory animate-pulse rounded-sm" />
            ))}
          </div>
        ) : items.length ? (
          <>
            <div
              data-testid="products-grid"
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
            >
              {items.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
            <div
              data-testid="product-count"
              className="mt-6 text-xs uppercase tracking-widest text-navy/50 text-center"
            >
              {`Showing ${items.length} ${items.length === 1 ? "product" : "products"}`}
            </div>
          </>
        ) : (
          <div className="text-center py-24 border border-dashed border-line rounded-sm text-navy/50">
            No products found in {active.l.toLowerCase()}.
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}

function ComingSoonBanner({ title, subtitle, note, img }) {
  return (
    <div
      data-testid="category-coming-soon"
      className="relative overflow-hidden rounded-sm border border-line bg-ivory"
    >
      <img src={img} alt={title} className="w-full h-72 sm:h-96 object-cover opacity-60" />
      <div className="absolute inset-0 bg-gradient-to-tr from-royal/85 via-royal/60 to-transparent" />
      <div className="absolute inset-0 flex flex-col items-start justify-center p-8 sm:p-14 text-white">
        <div className="text-[10px] uppercase tracking-[0.4em] text-gold mb-2">
          Coming Soon
        </div>
        <div className="font-serif text-4xl sm:text-5xl leading-tight">{title}</div>
        <div className="mt-2 font-serif text-lg text-gold">{subtitle}</div>
        <p className="mt-4 text-white/80 max-w-lg text-sm leading-relaxed">{note}</p>
      </div>
    </div>
  );
}
